import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import Layout from "../../layout/Layout";
import {
  downloadDiibExcelFile,
  getDiibImportExportGroupDetail,
  getDiibImportExportGroupList,
  toggleDiibImportExportGroupStatus,
  uploadDiibImportExportGroupMappingExcel,
  uploadDiibTemporarySalesAndTcgbExcel,
} from "../../../api/muhasebe/diibApi";

const getMuhasebeFileName = (filePathOrName) => {
  if (!filePathOrName) return "";

  const value = String(filePathOrName).trim();
  const normalized = value.replace(/[?#].*$/, "").replace(/\\/g, "/");

  return normalized.split("/").pop() || "";
};

const FORMAT_EXCEL_FILES = [
  {
    title: "Satış Formatı",
    fileName: "satis.xlsx",
  },
  {
    title: "TCGB Formatı",
    fileName: "tcgb.xlsx",
  },
];

function DiibTemporaryClosurePage() {
  const [search, setSearch] = useState("");
  const [diibNoFilter, setDiibNoFilter] = useState("");

  const [groups, setGroups] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
  });

  const [loadingList, setLoadingList] = useState(false);

  const [mappingExcelFile, setMappingExcelFile] = useState(null);
  const [mappingUploading, setMappingUploading] = useState(false);
  const [mappingResult, setMappingResult] = useState(null);

  const [salesExcelFile, setSalesExcelFile] = useState(null);
  const [tcgbExcelFile, setTcgbExcelFile] = useState(null);
  const [salesDiibNo, setSalesDiibNo] = useState("");
  const [salesUploading, setSalesUploading] = useState(false);
  const [salesResult, setSalesResult] = useState(null);
  const [downloadingFileName, setDownloadingFileName] = useState("");

  const [detailModal, setDetailModal] = useState({
    show: false,
    loading: false,
    data: null,
  });

  const [feedback, setFeedback] = useState({
    show: false,
    type: "success",
    message: "",
  });

  const showFeedback = (type, message) => {
    setFeedback({
      show: true,
      type,
      message,
    });
  };

  const getErrorTitle = (type) => {
    const errorMap = {
      DIIB_MAPPING_EMPTY_EXCEL: "Dosya boş",
      DIIB_MAPPING_HEADER_NOT_FOUND: "Başlık satırı bulunamadı",
      DIIB_MAPPING_MISSING_DIIB_NO: "DİİB numarası eksik",
      DIIB_MAPPING_INVALID_DOCUMENT_DATE: "Belge tarihi hatalı",
      DIIB_MAPPING_INVALID_IMPORT_END_DATE: "İthalat bitiş tarihi hatalı",
      DIIB_MAPPING_INVALID_EXPORT_END_DATE: "İhracat bitiş tarihi hatalı",
      DIIB_MAPPING_MISSING_GROUP_NO: "Grup numarası eksik",
      DIIB_MAPPING_MISSING_IMPORT_LINE_CODE: "İthalat satır kodu eksik",
      DIIB_MAPPING_INVALID_IMPORT_GTIP: "İthalat GTİP hatalı",
      DIIB_MAPPING_MISSING_EXPORT_LINE_CODE: "İhracat satır kodu eksik",
      DIIB_MAPPING_INVALID_EXPORT_GTIP: "İhracat GTİP hatalı",
      DIIB_MAPPING_IMPORT_LINE_CODE_MISMATCH:
        "Aynı grup içinde ithalat satır kodu farklı",
      DIIB_MAPPING_IMPORT_GTIP_MISMATCH: "Aynı grup içinde ithalat GTİP farklı",
      DIIB_MAPPING_DUPLICATE_EXPORT_LINE: "Aynı ihracat satırı tekrar yazılmış",

      SALES_EXCEL: "Satış Excel dosyası",
      TCGB_EXCEL: "Gümrük beyanname Excel dosyası",
      CLOSURE: "Kapatma işlemi",

      EMPTY_SALES_EXCEL: "Satış dosyası boş",
      SALES_HEADER_NOT_FOUND: "Satış dosyasında başlık satırı bulunamadı",
      SALES_MISSING_COLUMN: "Satış dosyasında kolon eksik",
      SALES_MISSING_INVOICE_NO: "Fatura numarası eksik",
      SALES_INVALID_QUANTITY: "Satış miktarı hatalı",

      EMPTY_TCGB_EXCEL: "Gümrük dosyası boş",
      TCGB_HEADER_NOT_FOUND: "Gümrük dosyasında başlık satırı bulunamadı",
      TCGB_MISSING_COLUMN: "Gümrük dosyasında kolon eksik",
      TCGB_MISSING_DECLARATION_NO: "Beyanname tescil no eksik",
      TCGB_MISSING_LINE_CODE: "Satır kodu eksik",
      TCGB_MISSING_GTIP8: "GTİP eksik",
      TCGB_INVALID_QUANTITY: "Miktar hatalı",

      SALE_DIIB_NO_MISSING: "Satış satırında DİİB numarası eksik",
      SALE_IMPORT_GTIP_INVALID: "Satış satırında GTİP okunamadı",
      DIIB_GROUP_NOT_FOUND_FOR_SALES_GTIP:
        "Satış GTİP için kayıtlı DİİB grubu bulunamadı",
      MULTIPLE_DIIB_GROUP_FOUND_FOR_SALES_GTIP:
        "Aynı satış GTİP için birden fazla DİİB grubu bulundu",
      INSUFFICIENT_TCGB_QUANTITY: "Gümrük miktarı yetersiz",
      SALE_GCB_QUANTITY_MISMATCH:
        "Satış miktarı ile beyanname miktarı eşit değil",
      OUTPUT_EXCEL_QUANTITY_MISMATCH:
        "Oluşturulan Excelde miktar kontrolü tutmadı",
    };

    return errorMap[type] || type || "Hata";
  };

  const getFileTypeName = (fileType) => {
    const fileTypeMap = {
      SALES_EXCEL: "Satış Excel",
      TCGB_EXCEL: "Gümrük Beyanname Excel",
      CLOSURE: "Kapatma İşlemi",
    };

    return fileTypeMap[fileType] || fileType || "—";
  };

  const fetchGroups = useCallback(
    async (page = 1) => {
      try {
        setLoadingList(true);

        const payload = {
          page,
          limit: pagination.limit,
          search,
          diibNo: diibNoFilter,
          sortBy: "groupNo",
          sortOrder: "asc",
        };

        const res = await getDiibImportExportGroupList(payload);

        if (res.data?.success) {
          setGroups(res.data.data || []);
          setPagination(
            res.data.pagination || {
              page,
              limit: pagination.limit,
              total: 0,
              totalPages: 1,
            }
          );
        } else {
          showFeedback(
            "error",
            res.data?.message || "Kayıtlı DİİB grupları alınamadı."
          );
        }
      } catch (error) {
        console.error("DİİB grup listesi alınamadı:", error);
        showFeedback(
          "error",
          "Kayıtlı DİİB grupları alınırken sunucu hatası oluştu."
        );
      } finally {
        setLoadingList(false);
      }
    },
    [search, diibNoFilter, pagination.limit]
  );

  useEffect(() => {
    fetchGroups(1);
  }, [fetchGroups]);

  const handleMappingFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setMappingExcelFile(file);
    setMappingResult(null);
  };

  const handleUploadMappingExcel = async () => {
    if (!mappingExcelFile) {
      showFeedback(
        "error",
        "Önce DİİB ithalat/ihracat eşleştirme Excel dosyası seçmelisin."
      );
      return;
    }

    try {
      setMappingUploading(true);
      setMappingResult(null);

      const res =
        await uploadDiibImportExportGroupMappingExcel(mappingExcelFile);

      if (res.data?.success) {
        setMappingResult(res.data.data);
        showFeedback(
          "success",
          res.data.message || "DİİB eşleştirme dosyası başarıyla yüklendi."
        );
        setMappingExcelFile(null);
        await fetchGroups(1);
      } else {
        showFeedback(
          "error",
          res.data?.message || "DİİB eşleştirme dosyası yüklenemedi."
        );
      }
    } catch (error) {
      console.error("DİİB eşleştirme dosyası yükleme hatası:", error);

      const errorData = error.response?.data;

      setMappingResult(errorData?.data || null);
      showFeedback(
        "error",
        errorData?.message || "DİİB eşleştirme dosyası yüklenirken hata oluştu."
      );
    } finally {
      setMappingUploading(false);
    }
  };

  const handleShowDetail = async (item) => {
    try {
      setDetailModal({
        show: true,
        loading: true,
        data: null,
      });

      const res = await getDiibImportExportGroupDetail({ id: item._id });

      if (res.data?.success) {
        setDetailModal({
          show: true,
          loading: false,
          data: res.data.data,
        });
      } else {
        setDetailModal({
          show: false,
          loading: false,
          data: null,
        });

        showFeedback("error", res.data?.message || "Detay alınamadı.");
      }
    } catch (error) {
      console.error("DİİB grup detay hatası:", error);

      setDetailModal({
        show: false,
        loading: false,
        data: null,
      });

      showFeedback("error", "DİİB grup detayı alınırken hata oluştu.");
    }
  };

  const handleCloseDetail = () => {
    setDetailModal({
      show: false,
      loading: false,
      data: null,
    });
  };

  const handleToggleStatus = async (item) => {
    try {
      const res = await toggleDiibImportExportGroupStatus({
        id: item._id,
        isActive: !item.isActive,
      });

      if (res.data?.success) {
        showFeedback("success", res.data.message || "Durum güncellendi.");
        await fetchGroups(pagination.page);
      } else {
        showFeedback("error", res.data?.message || "Durum güncellenemedi.");
      }
    } catch (error) {
      console.error("DİİB durum güncelleme hatası:", error);
      showFeedback("error", "Durum güncellenirken hata oluştu.");
    }
  };

  const handleSalesFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setSalesExcelFile(file);
    setSalesResult(null);
  };

  const handleTcgbFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setTcgbExcelFile(file);
    setSalesResult(null);
  };

  const handleUploadSalesAndTcgb = async () => {
    if (!salesExcelFile) {
      showFeedback("error", "Satış Excel dosyası seçmelisin.");
      return;
    }

    if (!tcgbExcelFile) {
      showFeedback("error", "Gümrük beyanname Excel dosyası seçmelisin.");
      return;
    }

    try {
      setSalesUploading(true);
      setSalesResult(null);

      const res = await uploadDiibTemporarySalesAndTcgbExcel({
        salesExcel: salesExcelFile,
        tcgbExcel: tcgbExcelFile,
        diibNo: salesDiibNo,
        options: {
          exportGtipFormat: "noDots",
          tcgbSortMode: "excelRow",
          failOnQuantityMismatch: true,
        },
      });

      if (res.data?.success) {
        setSalesResult(res.data.data);
        showFeedback(
          "success",
          res.data.message || "Kapatma dosyası başarıyla oluşturuldu."
        );
        setSalesExcelFile(null);
        setTcgbExcelFile(null);
      } else {
        showFeedback(
          "error",
          res.data?.message || "Kapatma işlemi başarısız oldu."
        );
      }
    } catch (error) {
      console.error("Satış + beyanname dosyası işleme hatası:", error);

      const errorData = error.response?.data;

      setSalesResult(errorData?.data || null);
      showFeedback(
        "error",
        errorData?.message ||
          "Satış ve gümrük beyanname dosyaları işlenirken hata oluştu."
      );
    } finally {
      setSalesUploading(false);
    }
  };

  const handleDownloadDiibFile = async (filePathOrName, preferredFileName) => {
    const fileName = getMuhasebeFileName(filePathOrName);

    if (!fileName) {
      showFeedback("error", "İndirilecek Excel dosyası bulunamadı.");
      return;
    }

    try {
      setDownloadingFileName(fileName);

      const response = await downloadDiibExcelFile(fileName);
      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");

      link.href = blobUrl;
      link.download = preferredFileName || fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("DİİB Excel dosyası indirilemedi:", error);
      showFeedback(
        "error",
        error.response?.status === 401 || error.response?.status === 403
          ? "Bu dosyayı indirmek için tekrar giriş yapmalısınız."
          : "Excel dosyası indirilemedi."
      );
    } finally {
      setDownloadingFileName("");
    }
  };

  const mappingErrors = useMemo(() => {
    if (!mappingResult?.errors) return [];
    return mappingResult.errors;
  }, [mappingResult]);

  const salesErrors = useMemo(() => {
    if (!salesResult?.errors) return [];
    return salesResult.errors;
  }, [salesResult]);

  const filledExcelFileName = useMemo(() => {
    const filledExcel = salesResult?.filledExcel;

    if (!filledExcel) return "";

    return getMuhasebeFileName(
      filledExcel.filePath ||
        filledExcel.path ||
        filledExcel.url ||
        filledExcel.fileName
    );
  }, [salesResult]);

  const formatDate = (value) => {
    if (!value) return "—";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return "—";

    return date.toLocaleDateString("tr-TR");
  };

  return (
    <Layout>
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h2 className="fw-bold mb-1">DİİB Geçici 17 Kapatma</h2>
          <div className="text-muted">
            Önce DİİB eşleştirme dosyasını yükleyin. Sonra satış Excel ve gümrük
            beyanname Excel dosyalarını yükleyerek kapatma dosyasını oluşturun.
          </div>
        </div>
      </div>

      <Card className="p-3 mb-4 shadow-sm border-0">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div>
            <div className="fw-semibold mb-1">Format Excel Dosyaları</div>
            <div className="text-muted small">
              Satış ve TCGB dosyalarını aşağıdaki örnek formatlara göre doldurup
              yükleyin.
            </div>
          </div>

          <div className="d-flex gap-2 flex-wrap">
            {FORMAT_EXCEL_FILES.map((file) => (
              <Button
                key={file.fileName}
                type="button"
                onClick={() =>
                  handleDownloadDiibFile(file.fileName, file.fileName)
                }
                disabled={downloadingFileName === file.fileName}
                variant="outline-primary"
                size="sm"
              >
                {downloadingFileName === file.fileName ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    İndiriliyor
                  </>
                ) : (
                  `${file.title} İndir`
                )}
              </Button>
            ))}
          </div>
        </div>
      </Card>

      <Card className="p-3 mb-4 shadow-sm border-0">
        <Row className="g-3">
          <Col md={4}>
            <div className="fw-semibold mb-1">
              1. DİİB İthalat / İhracat Eşleştirme Dosyası
            </div>
            <div className="text-muted small">
              Bu dosya, hangi ithalat satırının hangi ihracat satırlarıyla
              kapatılabileceğini sisteme tanıtır.
            </div>
          </Col>

          <Col md={5}>
            <Form.Group>
              <Form.Label>Eşleştirme Excel Dosyası</Form.Label>
              <Form.Control
                type="file"
                accept=".xlsx,.xls"
                onChange={handleMappingFileChange}
              />
              {mappingExcelFile && (
                <div className="small text-muted mt-1">
                  Seçilen dosya: {mappingExcelFile.name}
                </div>
              )}
            </Form.Group>
          </Col>

          <Col md={3} className="d-flex align-items-end justify-content-end">
            <Button
              variant="primary"
              onClick={handleUploadMappingExcel}
              disabled={mappingUploading}
            >
              {mappingUploading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Yükleniyor
                </>
              ) : (
                "Eşleştirme Dosyasını Yükle"
              )}
            </Button>
          </Col>
        </Row>

        {mappingResult && (
          <div className="mt-3">
            <Alert
              variant={mappingErrors.length ? "danger" : "success"}
              className="mb-0"
            >
              <div className="fw-semibold mb-2">
                Eşleştirme Dosyası Yükleme Sonucu
              </div>

              <Row className="g-2">
                <Col md={3}>
                  Okunan satır:{" "}
                  {mappingResult.excelRowCount || mappingResult.rowCount || 0}
                </Col>
                <Col md={3}>
                  Oluşturulan grup: {mappingResult.groupCount || 0}
                </Col>
                <Col md={3}>
                  Yeni eklenen kayıt: {mappingResult.insertedCount || 0}
                </Col>
                <Col md={3}>
                  Güncellenen kayıt: {mappingResult.modifiedCount || 0}
                </Col>
              </Row>

              {mappingErrors.length > 0 && (
                <div className="mt-3">
                  <div className="fw-semibold">
                    Düzeltilmesi Gereken Satırlar
                  </div>
                  <Table size="sm" responsive className="mt-2 mb-0">
                    <thead>
                      <tr>
                        <th>Excel Satırı</th>
                        <th>Hata Türü</th>
                        <th>Açıklama</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mappingErrors.slice(0, 20).map((error, index) => (
                        <tr key={`${error.rowNumber}-${index}`}>
                          <td>{error.rowNumber || "—"}</td>
                          <td>{getErrorTitle(error.type)}</td>
                          <td>{error.message || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Alert>
          </div>
        )}
      </Card>

      <Card className="p-3 mb-4 shadow-sm border-0">
        <Row className="align-items-end g-3">
          <Col md={4}>
            <Form.Label>Genel Arama</Form.Label>
            <Form.Control
              type="text"
              placeholder="DİİB no, grup no, GTİP veya satır kodu ara"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Col>

          <Col md={3}>
            <Form.Label>DİİB Numarası</Form.Label>
            <Form.Control
              type="text"
              placeholder="Örn: 2022/D1-01385"
              value={diibNoFilter}
              onChange={(e) => setDiibNoFilter(e.target.value)}
            />
          </Col>

          <Col md={5} className="text-end">
            <Button variant="outline-secondary" onClick={() => fetchGroups(1)}>
              Listeyi Yenile
            </Button>
          </Col>
        </Row>
      </Card>

      <Card className="shadow-sm border-0 mb-4">
        <div className="p-3 border-bottom d-flex justify-content-between align-items-center">
          <div>
            <div className="fw-semibold">
              2. Kayıtlı DİİB Eşleştirme Grupları
            </div>
            <div className="text-muted small">
              Sistemde kayıtlı toplam grup: {pagination.total || groups.length}
            </div>
          </div>

          {loadingList && <Spinner size="sm" />}
        </div>

        <Table hover responsive className="mb-0 align-middle">
          <thead className="table-light">
            <tr>
              <th>DİİB Numarası</th>
              <th>Grup No</th>
              <th>İthalat Satır Kodu</th>
              <th>İthalat GTİP</th>
              <th>Bağlı İhracat Satırı</th>
              <th>Belge Tarihi</th>
              <th>Durum</th>
              <th className="text-end">İşlem</th>
            </tr>
          </thead>

          <tbody>
            {groups.length > 0 ? (
              groups.map((item) => (
                <tr key={item._id}>
                  <td>{item.diibNo}</td>
                  <td>
                    <Badge bg="secondary">{item.groupNo}</Badge>
                  </td>
                  <td>{item.importLineCode || "—"}</td>
                  <td>{item.importGtip8 || "—"}</td>
                  <td>{item.exportLines?.length || 0}</td>
                  <td>{formatDate(item.documentDate)}</td>
                  <td>
                    <Badge bg={item.isActive ? "success" : "secondary"}>
                      {item.isActive ? "Aktif" : "Pasif"}
                    </Badge>
                  </td>
                  <td className="text-end">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-2"
                      onClick={() => handleShowDetail(item)}
                    >
                      Detay
                    </Button>

                    <Button
                      variant={
                        item.isActive ? "outline-warning" : "outline-success"
                      }
                      size="sm"
                      onClick={() => handleToggleStatus(item)}
                    >
                      {item.isActive ? "Pasif Yap" : "Aktif Yap"}
                    </Button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center text-muted py-4">
                  Kayıt bulunamadı
                </td>
              </tr>
            )}
          </tbody>
        </Table>

        <div className="p-3 border-top d-flex justify-content-between align-items-center">
          <div className="text-muted small">
            Sayfa {pagination.page || 1} / {pagination.totalPages || 1}
          </div>

          <div>
            <Button
              variant="outline-secondary"
              size="sm"
              className="me-2"
              disabled={(pagination.page || 1) <= 1}
              onClick={() => fetchGroups((pagination.page || 1) - 1)}
            >
              Önceki
            </Button>

            <Button
              variant="outline-secondary"
              size="sm"
              disabled={(pagination.page || 1) >= (pagination.totalPages || 1)}
              onClick={() => fetchGroups((pagination.page || 1) + 1)}
            >
              Sonraki
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-3 mb-4 shadow-sm border-0">
        <Row className="g-3">
          <Col md={12}>
            <div className="fw-semibold mb-1">
              3. Satış Excel ve Gümrük Beyanname Excel ile Kapatma
            </div>
            <div className="text-muted small">
              Satış Excel dosyasındaki DİİB numarası ve GTİP bilgisine göre
              sistemdeki eşleştirme grubu bulunur. Ardından uygun gümrük
              beyanname satırları kullanılarak kapatma dosyası oluşturulur.
            </div>
          </Col>

          <Col md={3}>
            <Form.Group>
              <Form.Label>DİİB Numarası</Form.Label>
              <Form.Control
                type="text"
                placeholder="Boş Bırakın"
                value={salesDiibNo}
                onChange={(e) => setSalesDiibNo(e.target.value)}
              />
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group>
              <Form.Label>Satış Excel Dosyası</Form.Label>
              <Form.Control
                type="file"
                accept=".xlsx,.xls"
                onChange={handleSalesFileChange}
              />
              {salesExcelFile && (
                <div className="small text-muted mt-1">
                  Seçilen dosya: {salesExcelFile.name}
                </div>
              )}
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group>
              <Form.Label>Gümrük Beyanname Excel Dosyası</Form.Label>
              <Form.Control
                type="file"
                accept=".xlsx,.xls"
                onChange={handleTcgbFileChange}
              />
              {tcgbExcelFile && (
                <div className="small text-muted mt-1">
                  Seçilen dosya: {tcgbExcelFile.name}
                </div>
              )}
            </Form.Group>
          </Col>

          <Col md={3} className="d-flex align-items-end justify-content-end">
            <Button
              variant="success"
              onClick={handleUploadSalesAndTcgb}
              disabled={salesUploading}
            >
              {salesUploading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  İşleniyor
                </>
              ) : (
                "Kapatma Dosyası Oluştur"
              )}
            </Button>
          </Col>
        </Row>

        {salesResult && (
          <div className="mt-3">
            <Alert
              variant={salesErrors.length ? "warning" : "success"}
              className="mb-0"
            >
              <div className="fw-semibold mb-2">Kapatma İşlem Sonucu</div>

              <Row className="g-2">
                <Col md={2}>
                  Satış satırı: {salesResult.summary?.salesRowCount || 0}
                </Col>
                <Col md={2}>
                  Beyanname satırı: {salesResult.summary?.tcgbRowCount || 0}
                </Col>
                <Col md={2}>
                  Tam kapanan: {salesResult.summary?.fullClosedCount || 0}
                </Col>
                <Col md={2}>
                  Kısmi kapanan: {salesResult.summary?.partialClosedCount || 0}
                </Col>
                <Col md={2}>
                  Kapanmayan: {salesResult.summary?.unmatchedSalesCount || 0}
                </Col>
                <Col md={2}>Hata: {salesResult.summary?.errorCount || 0}</Col>
              </Row>

              {filledExcelFileName && (
                <div className="mt-3">
                  <Button
                    type="button"
                    onClick={() =>
                      handleDownloadDiibFile(
                        filledExcelFileName,
                        salesResult.filledExcel?.fileName
                      )
                    }
                    disabled={downloadingFileName === filledExcelFileName}
                    variant="success"
                    size="sm"
                  >
                    {downloadingFileName === filledExcelFileName
                      ? "İndiriliyor"
                      : "Oluşturulan Excel Dosyasını İndir"}
                  </Button>

                  <div className="small text-muted mt-1">
                    Dosya:{" "}
                    {salesResult.filledExcel?.fileName ||
                      salesResult.filledExcel?.path ||
                      "kapatma-excel.xlsx"}
                  </div>
                </div>
              )}

              {salesErrors.length > 0 && (
                <div className="mt-3">
                  <div className="fw-semibold">
                    Kontrol Edilmesi Gereken Satırlar
                  </div>

                  <Table size="sm" responsive className="mt-2 mb-0">
                    <thead>
                      <tr>
                        <th>Excel Satırı</th>
                        <th>Dosya / İşlem</th>
                        <th>Hata Türü</th>
                        <th>Açıklama</th>
                      </tr>
                    </thead>

                    <tbody>
                      {salesErrors.slice(0, 30).map((error, index) => (
                        <tr key={`${error.rowNumber}-${index}`}>
                          <td>{error.rowNumber || "—"}</td>
                          <td>{getFileTypeName(error.fileType)}</td>
                          <td>{getErrorTitle(error.type)}</td>
                          <td>{error.message || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Alert>
          </div>
        )}
      </Card>

      <Modal
        show={detailModal.show}
        onHide={handleCloseDetail}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>DİİB Eşleştirme Grup Detayı</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {detailModal.loading ? (
            <div className="text-center py-4">
              <Spinner />
            </div>
          ) : detailModal.data ? (
            <>
              <Row className="mb-3 g-2">
                <Col md={4}>
                  <div className="text-muted small">DİİB Numarası</div>
                  <div className="fw-semibold">{detailModal.data.diibNo}</div>
                </Col>

                <Col md={2}>
                  <div className="text-muted small">Grup No</div>
                  <div className="fw-semibold">{detailModal.data.groupNo}</div>
                </Col>

                <Col md={3}>
                  <div className="text-muted small">İthalat Satır Kodu</div>
                  <div className="fw-semibold">
                    {detailModal.data.importLineCode}
                  </div>
                </Col>

                <Col md={3}>
                  <div className="text-muted small">İthalat GTİP</div>
                  <div className="fw-semibold">
                    {detailModal.data.importGtip8}
                  </div>
                </Col>
              </Row>

              <div className="fw-semibold mb-2">
                Bu ithalat satırına bağlı ihracat satırları
              </div>

              <Table responsive bordered size="sm">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>İhracat Satır Kodu</th>
                    <th>İhracat GTİP</th>
                    <th>Madde Adı</th>
                  </tr>
                </thead>

                <tbody>
                  {detailModal.data.exportLines?.length > 0 ? (
                    detailModal.data.exportLines.map((line, index) => (
                      <tr key={`${line.exportLineCode}-${index}`}>
                        <td>{index + 1}</td>
                        <td>{line.exportLineCode || line.lineCode || "—"}</td>
                        <td>{line.exportGtip8 || line.gtip8 || "—"}</td>
                        <td>
                          {line.exportMaterialName || line.materialName || "—"}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center text-muted">
                        Bu gruba bağlı ihracat satırı bulunamadı
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </>
          ) : (
            <div className="text-muted">Detay bulunamadı.</div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDetail}>
            Kapat
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={feedback.show}
        onHide={() => setFeedback({ ...feedback, show: false })}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {feedback.type === "success" ? "Başarılı" : "Hata"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p className="mb-0">{feedback.message}</p>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setFeedback({ ...feedback, show: false })}
          >
            Kapat
          </Button>
        </Modal.Footer>
      </Modal>
    </Layout>
  );
}

export default DiibTemporaryClosurePage;
